import React, { useEffect, useRef, useState } from "react";
import { Drawer, Dropdown, Input, Tooltip } from "antd";
import { DeleteOutlined, DownOutlined, EditOutlined, EyeOutlined, MinusCircleOutlined, MoreOutlined } from "@ant-design/icons";
import { getS3Url, getTimeAgoFromString } from "../../util/utils";
import { mobileState } from "../../state/atoms";
import { useRecoilValue } from "recoil";
import { useOnClickOutside } from "usehooks-ts";
import useFocus from "../../hooks/useFocus";
import { SearchableUpload } from "./EditScreenContentModal";
import QRDisplayUploadsApi from "../../api/QRDisplayUploadsApi";
import { DisplayUploadDto } from "../../domain/DisplayUploadDto";
import { DisplayUploadRenameDto } from "../../domain/DisplayUploadRenameDto";
import MoveFolderIcon from "../icons/MoveFolderIcon";

interface UploadCardProps {
  it: DisplayUploadDto;
  setDeleteModal: (visible: boolean) => void;
  setSelected: (selected: DisplayUploadDto) => void;
  setPreviewModal: (visible: boolean) => void;
  uploads: SearchableUpload[];
  setUploads: (uploads: SearchableUpload[]) => void;
  onMoveToFolder?: (upload: DisplayUploadDto) => void;
  onRemoveFromFolder?: (upload: DisplayUploadDto) => void;
  inFolder?: boolean;
}

export default function UploadCard({ it, setDeleteModal, setSelected, setPreviewModal, uploads, setUploads, onMoveToFolder, onRemoveFromFolder, inFolder }: UploadCardProps) {
  const [isMenuVisible, setMenuVisible] = useState(false);
  const menuRef = useRef(null);

  const mobile = useRecoilValue(mobileState);
  const [isMobileMenuVisible, setMobileMenuVisible] = useState(false);

  const [isRenaming, setRenaming] = useState(false);
  const [name, setName] = useState("");
  const [renameInputRef, setRenameInputFocus] = useFocus();

  function hideDropdown() {
    setMenuVisible(false);
  }

  useOnClickOutside(menuRef, hideDropdown);

  useEffect(() => {
    setName(it.name ?? "");
  }, [it]);

  function startRenaming() {
    setRenaming(true);
    (setRenameInputFocus as () => void)();
  }

  async function rename() {
    setRenaming(false);
    try {
      await QRDisplayUploadsApi.updateDisplayUploadName({ uploadId: it.id!, newName: name } as DisplayUploadRenameDto);
      uploads.find(u => u.id! === it.id!)!.name = name;
      setUploads([...uploads]);
    } catch (e) {
      console.error(e);
    }
  }

  function deleteUpload(it: DisplayUploadDto) {
    setSelected(it);
    setDeleteModal(true);
    hideDropdown();
    setMobileMenuVisible(false);
  }

  function openPreview() {
    setSelected(it);
    setPreviewModal(true);
    hideDropdown();
  }

  const menuContent = (<div ref={menuRef}>
    <div className="info">
      {mobile && <h3>{it.name}</h3>}
      Uploaded {getTimeAgoFromString(it.createdDate!)}
    </div>
    <ul>
      <li onClick={() => openPreview()}>
        <EyeOutlined /> Preview
      </li>
      {onMoveToFolder && <li onClick={() => { onMoveToFolder(it); hideDropdown(); setMobileMenuVisible(false); }}>
        <MoveFolderIcon /> Move to folder
      </li>}
      {inFolder && onRemoveFromFolder && <li onClick={() => { onRemoveFromFolder(it); hideDropdown(); setMobileMenuVisible(false); }}>
        <MinusCircleOutlined /> Remove from folder
      </li>}
      <li onClick={() => deleteUpload(it)}><DeleteOutlined />Delete</li>
    </ul>
  </div>)

  return <li key={it.id} className="upload-card">
    {!mobile ?
      <Dropdown
        visible={isMenuVisible}
        getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}
        overlayClassName="upload-edit-menu"
        overlay={menuContent}
      >
        <Tooltip title="More" placement="bottom" getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}>
          <div className="upload-edit-trigger">
            <MoreOutlined onClick={() => { setMenuVisible(!isMenuVisible) }} />
          </div>
        </Tooltip>
      </Dropdown>
      : <>
        <div className="upload-edit-trigger">
          <MoreOutlined onClick={() => { setMobileMenuVisible(!isMobileMenuVisible) }} />
        </div>
        <Drawer
          placement="bottom"
          visible={isMobileMenuVisible}
          className="mobile-bottom-menu upload-edit-menu"
          maskClosable={true}
          closeIcon={<DownOutlined/>}
          onClose={() => setMobileMenuVisible(false)}
        >
          {menuContent}
        </Drawer>
      </>
    }

    <div className="img-cont">
      <img src={getS3Url(it.path!)} alt={it.name} />
    </div>

    {!isRenaming && (
      <div
        className="edit-name"
        onClick={startRenaming}
      >
        <p>{name ? name : it.name}</p>
        <EditOutlined />
      </div>
    )}
    <div className={`editing-name ${isRenaming ? "show" : ""}`}>
      <Input
        ref={renameInputRef}
        value={name}
        onChange={(ev) => setName(ev.target.value)}
        onBlur={rename}
      />
    </div>
  </li>
}
